'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { ChevronLeft, Tv, Share2, Play, Calendar, Link2, Loader2 } from 'lucide-react';
import BottomNav from '@/components/BottomNav';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

const YoutubeIcon = ({ size = 24, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 0 0 1.46 6.42 29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.95C5.12 20 12 20 12 20s6.88 0 8.59-.47a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/>
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill={color} stroke="none"/>
  </svg>
);

interface TVVideo {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  offer_id: string | null;
  published_at: string;
  is_published: boolean;
  offers?: { id: string; title: string } | null;
}

export default function TVPageContent() {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [videos, setVideos] = useState<TVVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('tv_videos')
        .select('*, offers(id, title)')
        .eq('is_published', true)
        .lte('published_at', new Date().toISOString())
        .order('published_at', { ascending: false });
      setVideos(data || []);
      setLoading(false);
    };
    load();
  }, []);

  const handleShare = (video: TVVideo) => {
    const url = `${window.location.origin}/tv`;
    if (navigator.share) {
      navigator.share({ title: video.title, url });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link kopiert!', { icon: '🔗', duration: 2000 });
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--background)' }}>
      {/* ── HEADER ── */}
      <div
        className="sticky top-0 z-40 px-4"
        style={{
          background: 'rgba(27,29,35,0.96)',
          borderBottom: '1px solid var(--border)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingTop: 'calc(env(safe-area-inset-top, 44px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <div className="flex items-center justify-between max-w-mobile mx-auto">
          <Link
            href="/"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            aria-label="Zurück zur Entdecken-Seite"
          >
            <ChevronLeft size={18} color="var(--card-foreground)" />
          </Link>

          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}
            >
              <Tv size={15} color="#ef4444" />
            </div>
            <span className="text-base font-bold" style={{ color: 'var(--card-foreground)' }}>
              WINEWOOD TV
            </span>
          </div>

          <div className="w-9" />
        </div>
      </div>

      <div className="px-4 pt-4 max-w-mobile mx-auto space-y-4">
        {/* ── INTRO BANNER ── */}
        <div
          className="rounded-app p-4"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          <h2 className="text-sm font-bold mb-1" style={{ color: 'var(--card-foreground)' }}>
            Abenteuer live erleben!
          </h2>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Schau dir an, wie WINEWOOD die Angebote und Abenteuer selbst ausprobiert. Entdecke neue
            Orte und lass dich inspirieren!
          </p>
        </div>

        {/* ── LOADING ── */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin" color="var(--primary)" />
          </div>
        )}

        {/* ── EMPTY ── */}
        {!loading && videos.length === 0 && (
          <div
            className="rounded-app p-8 text-center"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
          >
            <Tv size={32} color="var(--muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm font-medium" style={{ color: 'var(--card-foreground)' }}>
              Noch keine Videos
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
              Schau bald wieder vorbei!
            </p>
          </div>
        )}

        {/* ── VIDEO CARDS ── */}
        {!loading && videos.map((video, index) => {
          const isYouTube = video.video_url?.includes('youtube.com/embed/');
          return (
            <div
              key={video.id}
              className="rounded-app overflow-hidden"
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                boxShadow: index === 0 ? '0 4px 20px rgba(0,0,0,0.4)' : '0 2px 10px rgba(0,0,0,0.25)',
              }}
            >
              {/* Video embed */}
              <div className="relative" style={{ paddingBottom: '56.25%', background: '#000' }}>
                {playingId === video.id && isYouTube ? (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`${video.video_url}?autoplay=1`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={video.title}
                  />
                ) : (
                  <>
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt={`WINEWOOD TV: ${video.title}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <YoutubeIcon size={40} color="rgba(255,255,255,0.2)" />
                      </div>
                    )}
                    {/* Overlay gradient */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 100%)',
                      }}
                    />

                    {/* Play button */}
                    <button
                      onClick={() => setPlayingId(video.id)}
                      className="absolute inset-0 flex items-center justify-center group"
                      aria-label={`${video.title} abspielen`}
                    >
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-150 group-active:scale-90"
                        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
                      >
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ background: '#ef4444' }}
                        >
                          <Play size={20} color="#fff" fill="#fff" className="ml-0.5" />
                        </div>
                      </div>
                    </button>

                    {/* Share button */}
                    <button
                      onClick={() => handleShare(video)}
                      className="absolute bottom-3 left-3 w-9 h-9 rounded-full flex items-center justify-center glass-button transition-all duration-150 active:scale-90"
                      aria-label={`${video.title} teilen`}
                    >
                      <Share2 size={15} color="white" />
                    </button>

                    {/* YouTube badge */}
                    <div
                      className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-pill text-[10px] font-bold"
                      style={{ background: 'rgba(239,68,68,0.85)', color: '#fff' }}
                    >
                      <YoutubeIcon size={10} />
                      YouTube
                    </div>
                  </>
                )}
              </div>

              {/* Video info */}
              <div className="p-3.5">
                <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--card-foreground)' }}>
                  {video.title}
                </h3>
                {video.description && (
                  <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    {video.description}
                  </p>
                )}

                <div className="flex items-center justify-between mt-2.5">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={11} color="var(--muted-foreground)" />
                    <span className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>
                      {new Date(video.published_at).toLocaleDateString('de-DE', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>

                  {video.offers && (
                    <Link
                      href={`/offer-detail-page?id=${video.offer_id}`}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-semibold transition-all duration-150 active:scale-95"
                      style={{
                        background: 'rgba(124,179,66,0.12)',
                        color: 'var(--primary)',
                        border: '1px solid rgba(124,179,66,0.25)',
                      }}
                    >
                      <Link2 size={10} />
                      {video.offers.title}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <BottomNav />
    </div>
  );
}
